import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  Title,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  Group,
  Avatar,
  ActionIcon,
  FileButton,
  Divider,
  Box,
  useMantineColorScheme,
} from '@mantine/core';
import { getFlatRoundedInputStyles, flatRoundedInputProps } from '../../styles/inputStyles';
import {
  IconCamera,
  IconTrash,
  IconCalendar,
  IconCheck,
  IconX,
  IconDeviceFloppy,
  IconRosetteDiscountCheckFilled,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  perfilUsuarioService,
  ProfileDto,
} from '../../services/perfilUsuarioService';
import { colorPorNombre, pastel, ink } from '../../utils/especialidad';
import { banderaPais, nombrePais } from '../../utils/paises';

const PersonalInfoContent: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);

  const inputProps = { ...flatRoundedInputProps, size: 'sm' as const };

  const [perfil, setPerfil] = useState<ProfileDto | null>(null);

  const [infoPersonal, setInfoPersonal] = useState({
    nombre: '',
    apellidos: '',
    telefono: '',
    bio: '',
    pais: '',
    tz: 'America/Monterrey',
    fechaNacimiento: '',
    genero: '',
  });

  const [infoAcademica, setInfoAcademica] = useState({
    universidad: '',
    anioGraduacion: undefined as number | undefined,
    especialidadInteres: '',
  });

  const [paises, setPaises] = useState<string[]>([]);
  const [timezones, setTimezones] = useState<{ [key: string]: string }>({});
  const [especialidades, setEspecialidades] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [perfilData, paisesData, timezonesData, especialidadesData] = await Promise.all([
          perfilUsuarioService.getMiPerfil(),
          perfilUsuarioService.getPaisesDisponibles().catch(() => []),
          perfilUsuarioService.getTimezonesDisponibles().catch(() => ({})),
          perfilUsuarioService.getEspecialidadesDisponibles().catch(() => []),
        ]);

        setPerfil(perfilData);
        setPaises(paisesData);
        setTimezones(timezonesData);
        setEspecialidades(especialidadesData);

        setInfoPersonal({
          nombre: perfilData.nombre || '',
          apellidos: perfilData.apellido || '',
          telefono: perfilData.telefono || '',
          bio: perfilData.bio || '',
          // Normalizamos a nombre por si viene como código ISO ("MX") del registro,
          // así el selector lo reconoce y al guardar queda en formato consistente.
          pais: nombrePais(perfilData.pais || ''),
          tz: perfilData.tz || 'America/Monterrey',
          fechaNacimiento: perfilData.fechaNacimiento || '',
          genero: perfilData.genero || '',
        });

        setInfoAcademica({
          universidad: perfilData.universidad || '',
          anioGraduacion: perfilData.anioGraduacion || undefined,
          especialidadInteres: perfilData.especialidadInteres || '',
        });
      } catch (error) {
        console.error('Error loading profile data:', error);
        notifications.show({
          color: 'red',
          title: 'Error',
          message: 'No se pudo cargar la información del perfil',
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Guardar todo (personal + académica) en una sola acción, como "editar mi perfil".
  const handleSaveAll = async () => {
    try {
      setSaving(true);
      const actualizado = await perfilUsuarioService.actualizarPerfil({
        nombre: infoPersonal.nombre,
        apellido: infoPersonal.apellidos,
        telefono: infoPersonal.telefono,
        bio: infoPersonal.bio,
        pais: infoPersonal.pais,
        tz: infoPersonal.tz,
        fechaNacimiento: infoPersonal.fechaNacimiento,
        genero: infoPersonal.genero,
        universidad: infoAcademica.universidad,
        anioGraduacion: infoAcademica.anioGraduacion,
        especialidadInteres: infoAcademica.especialidadInteres,
      });
      setPerfil(actualizado);
      notifications.show({
        color: 'green',
        title: 'Guardado',
        message: 'Tu perfil se actualizó correctamente',
        icon: <IconCheck size={16} />,
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo guardar el perfil',
        icon: <IconX size={16} />,
      });
    } finally {
      setSaving(false);
    }
  };

  // Validaciones de subida del avatar (el backend ya valida también).
  const MAX_AVATAR_MB = 5;
  const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  const handleUploadAvatar = async (file: File | null) => {
    if (!file) return;

    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      notifications.show({
        color: 'red',
        title: 'Formato no permitido',
        message: 'Sube una imagen JPG, PNG, WebP o GIF',
        icon: <IconX size={16} />,
      });
      return;
    }
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_AVATAR_MB) {
      notifications.show({
        color: 'red',
        title: 'Imagen demasiado grande',
        message: `El máximo permitido es ${MAX_AVATAR_MB} MB (este archivo pesa ${sizeMb.toFixed(1)} MB)`,
        icon: <IconX size={16} />,
      });
      return;
    }

    try {
      setUploadingAvatar(true);
      const actualizado = await perfilUsuarioService.subirAvatar(file);
      setPerfil(actualizado);
      notifications.show({ color: 'green', title: 'Éxito', message: 'Avatar actualizado', icon: <IconCheck size={16} /> });
    } catch (error: any) {
      notifications.show({ color: 'red', title: 'Error', message: error?.response?.data?.message || 'No se pudo subir el avatar', icon: <IconX size={16} /> });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setUploadingAvatar(true);
      const actualizado = await perfilUsuarioService.eliminarAvatar();
      setPerfil(actualizado);
      notifications.show({ color: 'green', title: 'Éxito', message: 'Avatar eliminado', icon: <IconCheck size={16} /> });
    } catch (error: any) {
      notifications.show({ color: 'red', title: 'Error', message: error?.response?.data?.message || 'No se pudo eliminar el avatar', icon: <IconX size={16} /> });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Paleta sobria (negro + beige, acento pastel), idéntica a la de "Mi perfil".
  const cardStyle: React.CSSProperties = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26';
  const mutedColor = colorScheme === 'dark' ? '#94a3b8' : '#8c8478';
  const dividerColor = colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,38,0.1)';
  const labelStyle: React.CSSProperties = {
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: mutedColor,
    fontFamily: 'Inter, sans-serif',
  };

  if (loading) {
    return (
      <Box p="xl">
        <Text style={{ color: mutedColor }}>Cargando información del perfil…</Text>
      </Box>
    );
  }

  const fullName = `${perfil?.nombre || ''} ${perfil?.apellido || ''}`.trim() || perfil?.username || '';
  const initials = `${(perfil?.nombre?.[0] || '')}${(perfil?.apellido?.[0] || '')}`.toUpperCase() || (perfil?.username?.[0] || '?').toUpperCase();
  const baseColor = colorPorNombre(infoAcademica.especialidadInteres || perfil?.especialidadInteres || '');
  const bannerColor = pastel(baseColor);
  const inkColor = ink(baseColor);

  return (
    <Box style={{ maxWidth: 720 }}>
      <style>{`
        @media (max-width: 768px) {
          .settings-hero-head { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      {/* ===== Hero (igual que Mi perfil) ===== */}
      <Box
        style={{
          ...cardStyle,
          borderRadius: 16,
          overflow: 'hidden',
          position: 'relative',
          marginBottom: '0.75rem',
        }}
      >
        <Box style={{ height: 76, backgroundColor: bannerColor }} />

        <Box style={{ padding: '0 1.1rem 1.1rem' }}>
          {/* Avatar superpuesto con botón de cámara para cambiar foto */}
          <Box style={{ position: 'relative', width: 88, marginTop: -44 }}>
            <Avatar
              size={88}
              src={perfil?.avatar}
              radius="50%"
              style={{
                border: `3px solid ${colorScheme === 'dark' ? '#1e293b' : '#f7f3ee'}`,
                backgroundColor: inkColor,
              }}
            >
              <Text fw={700} style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.6rem', color: 'white' }}>
                {initials}
              </Text>
            </Avatar>

            <FileButton onChange={handleUploadAvatar} accept="image/png,image/jpeg,image/webp,image/gif">
              {(props) => (
                <ActionIcon
                  {...props}
                  loading={uploadingAvatar}
                  radius="xl"
                  size="sm"
                  variant="filled"
                  color="dark"
                  title="Cambiar foto"
                  style={{ position: 'absolute', bottom: 0, right: 0 }}
                >
                  <IconCamera size={14} />
                </ActionIcon>
              )}
            </FileButton>
          </Box>

          <Group className="settings-hero-head" justify="space-between" align="flex-start" wrap="nowrap" mt={6}>
            <Box style={{ minWidth: 0 }}>
              <Group gap={6} align="center" wrap="nowrap">
                <Title
                  order={3}
                  ta="left"
                  style={{
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    letterSpacing: '-0.02em',
                    color: textColor,
                    margin: 0,
                  }}
                >
                  {fullName}
                </Title>
                {perfil?.emailVerificado && (
                  <IconRosetteDiscountCheckFilled size={20} color="#1d9bf0" style={{ flexShrink: 0 }} aria-label="Cuenta verificada" />
                )}
              </Group>
              <Text size="xs" ta="left" style={{ fontFamily: 'Inter, sans-serif', color: mutedColor }}>
                @{perfil?.username}
              </Text>
              {perfil?.fechaRegistro && (
                <Group gap={5} align="center" mt={6}>
                  <IconCalendar size={14} color={mutedColor} />
                  <Text size="xs" ta="left" style={{ fontFamily: 'Inter, sans-serif', color: mutedColor }}>
                    Se unió en{' '}
                    {new Date(perfil.fechaRegistro).toLocaleDateString('es-MX', { year: 'numeric', month: 'long' })}
                  </Text>
                </Group>
              )}
            </Box>

            {perfil?.avatar && (
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconTrash size={14} />}
                onClick={handleDeleteAvatar}
                loading={uploadingAvatar}
                style={{ flexShrink: 0, color: mutedColor }}
              >
                Quitar foto
              </Button>
            )}
          </Group>
        </Box>
      </Box>

      {/* ===== Campos editables (sueltos, alineados a la izquierda) ===== */}
      <Box px={4}>
        {/* Sobre mí */}
        <Box mb="lg">
          <Text size="xs" fw={700} ta="left" mb={6} style={labelStyle}>
            Sobre mí
          </Text>
          <Textarea
            placeholder="Cuéntale a los demás sobre ti…"
            value={infoPersonal.bio}
            onChange={(e) => setInfoPersonal({ ...infoPersonal, bio: e.target.value })}
            minRows={3}
            maxRows={6}
            autosize
            {...inputProps}
            styles={getFlatRoundedInputStyles(colorScheme)}
          />
        </Box>

        <Divider color={dividerColor} />

        {/* Datos personales */}
        <Box my="lg">
          <Text size="xs" fw={700} ta="left" mb="md" style={labelStyle}>
            Datos personales
          </Text>
          <Stack gap="sm">
            <Group grow>
              <TextInput
                label="Nombre"
                value={infoPersonal.nombre}
                onChange={(e) => setInfoPersonal({ ...infoPersonal, nombre: e.target.value })}
                required
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
              <TextInput
                label="Apellidos"
                value={infoPersonal.apellidos}
                onChange={(e) => setInfoPersonal({ ...infoPersonal, apellidos: e.target.value })}
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
            </Group>
            <Group grow>
              <TextInput
                type="date"
                label="Fecha de nacimiento"
                value={infoPersonal.fechaNacimiento}
                onChange={(e) => setInfoPersonal({ ...infoPersonal, fechaNacimiento: e.target.value })}
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
              <Select
                label="Género"
                data={[
                  { value: 'M', label: 'Masculino' },
                  { value: 'F', label: 'Femenino' },
                  { value: 'Otro', label: 'Otro' },
                  { value: 'Prefiero no decir', label: 'Prefiero no decir' },
                ]}
                value={infoPersonal.genero}
                onChange={(value) => setInfoPersonal({ ...infoPersonal, genero: value || '' })}
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
            </Group>
            <Group grow>
              <Select
                label="País"
                data={paises.map((p) => ({ value: p, label: `${banderaPais(p)} ${p}`.trim() }))}
                value={infoPersonal.pais}
                onChange={(value) => setInfoPersonal({ ...infoPersonal, pais: value || '' })}
                searchable
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
              <Select
                label="Zona horaria"
                data={Object.entries(timezones).map(([key, label]) => ({ value: key, label }))}
                value={infoPersonal.tz}
                onChange={(value) => setInfoPersonal({ ...infoPersonal, tz: value || 'America/Monterrey' })}
                searchable
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Correo"
                value={perfil?.email || ''}
                disabled
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
              <TextInput
                label="Teléfono"
                value={infoPersonal.telefono}
                disabled
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
            </Group>
          </Stack>
        </Box>

        <Divider color={dividerColor} />

        {/* Información académica */}
        <Box mt="lg">
          <Text size="xs" fw={700} ta="left" mb="md" style={labelStyle}>
            Información académica
          </Text>
          <Stack gap="sm">
            <Group grow>
              <TextInput
                label="Universidad"
                placeholder="Tu universidad o institución"
                value={infoAcademica.universidad}
                onChange={(e) => setInfoAcademica({ ...infoAcademica, universidad: e.target.value })}
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
              <NumberInput
                label="Año de graduación"
                placeholder="2020"
                min={1950}
                max={new Date().getFullYear() + 10}
                value={infoAcademica.anioGraduacion}
                onChange={(value) => setInfoAcademica({ ...infoAcademica, anioGraduacion: value as number })}
                {...inputProps}
                styles={getFlatRoundedInputStyles(colorScheme)}
              />
            </Group>
            <Select
              label="Especialidad de interés"
              placeholder="Seleccionar especialidad"
              data={especialidades.map((esp) => ({ value: esp, label: esp }))}
              value={infoAcademica.especialidadInteres}
              onChange={(value) => setInfoAcademica({ ...infoAcademica, especialidadInteres: value || '' })}
              searchable
              {...inputProps}
              styles={getFlatRoundedInputStyles(colorScheme)}
            />
          </Stack>
        </Box>

        {/* Guardar */}
        <Group justify="flex-start" mt="xl">
          <Button color="dark" radius="md" onClick={handleSaveAll} loading={saving} leftSection={<IconDeviceFloppy size={16} />}>
            Guardar cambios
          </Button>
        </Group>
      </Box>
    </Box>
  );
};

export default PersonalInfoContent;
